import { useEffect, VFC } from 'react'
import { useFormContext } from 'react-hook-form'
import { MasterDataStore, useMasterData } from 'src/api/masterData'
import styled from 'styled-components'
import {
  formInputStyle,
  FormItemProps,
  FormItemWrapper,
  invalidStyle,
} from './common'
import { softOrange, safetyOrange, errorRed } from 'src/styles/colors'

type FormSelectProps = FormItemProps & {
  options: {
    value: string | undefined
    label: string
  }[]
  placeholder?: string
}

export const FormSelect: VFC<FormSelectProps> = ({
  placeholder,
  options,
  ...props
}) => {
  const { id } = props
  const { register, formState } = useFormContext()
  const invalid = formState.errors[`${id}`]

  return (
    <FormItemWrapper {...props}>
      <Select
        id={id}
        placeholder={placeholder}
        {...register(id, {
          required: props.optional ? false : `${props.label}は必須です`,
        })}
        defaultValue=""
        invalid={invalid}
      >
        <option value="" disabled={!props.optional} />
        {options.map(({ value, label }) => (
          <option key={value} value={value} label={label}>
            {label}
          </option>
        ))}
      </Select>
      <ErrorMessage>{formState.errors[`${id}`]?.message}</ErrorMessage>
    </FormItemWrapper>
  )
}

export const MasterDataSelect: VFC<
  Omit<FormSelectProps, 'options'> & { type: keyof MasterDataStore }
> = ({ type, placeholder, ...props }) => {
  const { id } = props
  const { data } = useMasterData()
  const options = data ? data[type] : []
  const { register, watch, setValue, formState, clearErrors } = useFormContext()
  const currentValue = watch(id)
  const invalid = formState.errors[`${id}`]

  useEffect(() => {
    register(id)
  }, [id, register])
  return (
    <FormItemWrapper {...props}>
      <Select
        id={id}
        placeholder={placeholder}
        onChange={({ target: { selectedIndex, options } }) => {
          const { value, label } = options[selectedIndex]
          setValue(id, { value: value || null, label })
          clearErrors(id)
        }}
        value={currentValue?.value || ''}
        invalid={invalid}
      >
        <option value="" disabled={!props.optional} />
        {options.map(({ value, label }) => (
          <option key={value} value={value} label={label}>
            {label}
          </option>
        ))}
      </Select>
      <ErrorMessage>{formState.errors[`${id}`]?.message}</ErrorMessage>
    </FormItemWrapper>
  )
}

export type BooleanSelectProps = FormSelectProps & {
  options: {
    value: 'true' | 'false' | ''
    label: string
  }[]
}

const toBool = (value: string) => {
  if (value === 'true') return true
  return false
}

export const BooleanSelect: VFC<BooleanSelectProps> = ({
  options,
  placeholder,
  ...props
}) => {
  const { id } = props
  const { register, watch, setValue, clearErrors, formState } = useFormContext()
  const currentValue = watch(id)

  const onChange = (value: string) => {
    setValue(id, toBool(value))
    clearErrors(id)
  }

  useEffect(() => {
    register(id)
  }, [id, register])

  return (
    <FormItemWrapper {...props}>
      <RadioDiv>
        {options.map(({ value, label }) => {
          return (
            <>
              {/* NOTE: input内でregisterを使用すると初期値が反映されないため、submit時にバリデーションを行う */}
              <input
                type="radio"
                name={id}
                id={value}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                checked={currentValue === toBool(value || 'false')}
              />
              <label htmlFor={value}>{label}</label>
            </>
          )
        })}
      </RadioDiv>
      <ErrorMessage>{formState.errors[`${id}`]?.message}</ErrorMessage>
    </FormItemWrapper>
  )
}

const RadioDiv = styled.div`
  width: 100%;
  display: flex;

  input {
    display: none;
  }

  label {
    cursor: pointer;
    width: 174px;
    margin: 0;
    padding: 12px 5px;
    border: 1px solid #cdcdcd;
    color: #555e64;
    font-size: 14px;
    text-align: center;
    line-height: 1;
    transition: 0.2s;
  }

  label:first-of-type {
    border-radius: 5px 0 0 5px;
  }

  label:last-of-type {
    border-radius: 0 5px 5px 0;
  }

  input[type='radio']:checked + label {
    background-color: ${softOrange};
    color: ${safetyOrange};
    border: 1px solid ${safetyOrange};
  }
`

const Select = styled.select<{ invalid: boolean }>`
  ${formInputStyle};
  ${({ invalid }) => invalid && invalidStyle};
`

const ErrorMessage = styled.p`
  margin-top: 2px;
  font-size: 13px;
  color: ${errorRed};
`
